import { Box, Chip } from '@mui/material';
import { useEffect, useState } from 'react';
import { Theme } from '@mui/material/styles';
import { SystemStyleObject } from '@mui/system';
import { mediaToneOptions, Tone, ToneColorMap } from '../constants/constants';
import PositiveIcon from '../../components/TrackerFilter/Icons/PositiveIcon';
import NeutralIcon from '../../components/TrackerFilter/Icons/NeutralIcon';
import NegativeIcon from '../../components/TrackerFilter/Icons/NegativeIcon';
import { calendarSnapshotChipTypography } from '../../components/Calendar/CalendarSnapshotChip';

interface ToneInputSelectionProps {
  selectedTones?: Tone[];
  onSelect?: (selectedTones: Tone[]) => void;
  multiSelect?: boolean;
  chipSx?: SystemStyleObject<Theme> | SystemStyleObject<Theme>[];
}

export default function ToneInputSelection(props: ToneInputSelectionProps) {
  const { selectedTones, onSelect, multiSelect, chipSx } = props;
  const [localSelectedTones, setLocalSelectedTones] = useState<Tone[]>([]);

  useEffect(() => {
    setLocalSelectedTones(selectedTones ?? []);
  }, [selectedTones]);

  const getChipIcon = (toneName: Tone) => {
    if (toneName === Tone.POSITIVE) {
      return <PositiveIcon />;
    } else if (toneName === Tone.NEGATIVE) {
      return <NegativeIcon />;
    } else if (toneName === Tone.NEUTRAL) {
      return <NeutralIcon />;
    }
  };

  const handleChipClick = (toneName: Tone) => {
    const isSelected = localSelectedTones.some((selectedTone) => selectedTone === toneName);

    let selectedChips: Tone[] = [];
    if (isSelected) {
      selectedChips = localSelectedTones.filter((selectedChip) => selectedChip !== toneName);
    } else if (multiSelect) {
      selectedChips = [...localSelectedTones, toneName];
    } else {
      selectedChips = [toneName];
    }

    setLocalSelectedTones(selectedChips);

    if (onSelect) {
      onSelect(selectedChips);
    }
  };

  const getChipSx = (toneName: Tone, isSelected: boolean): SystemStyleObject<Theme> => {
    const toneStyles: Record<
      Tone,
      { selectedBg: string; selectedBorder: string; selectedText: string; inactiveBorder: string }
    > = {
      [Tone.POSITIVE]: {
        selectedBg: '#EAF7EE',
        selectedBorder: '#B7DFBE',
        selectedText: '#216A35',
        inactiveBorder: '#B7DFBE',
      },
      [Tone.NEUTRAL]: {
        selectedBg: '#F2F4F7',
        selectedBorder: '#D5D9E1',
        selectedText: '#111827',
        inactiveBorder: '#DDE2E9',
      },
      [Tone.NEGATIVE]: {
        selectedBg: '#FDEEEE',
        selectedBorder: '#F4C7C3',
        selectedText: '#912018',
        inactiveBorder: '#F0D5D2',
      },
    };

    const toneStyle = toneStyles[toneName];

    const baseSx: SystemStyleObject<Theme> = {
      height: 28,
      px: 1.25,
      borderRadius: '999px',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: isSelected ? toneStyle.selectedBorder : toneStyle.inactiveBorder,
      backgroundColor: isSelected ? toneStyle.selectedBg : '#FFFFFF',
      color: isSelected ? toneStyle.selectedText : '#111111',
      fontFamily: calendarSnapshotChipTypography.fontFamily,
      fontSize: calendarSnapshotChipTypography.fontSize,
      fontWeight: calendarSnapshotChipTypography.fontWeight,
      lineHeight: calendarSnapshotChipTypography.lineHeight,
      letterSpacing: calendarSnapshotChipTypography.letterSpacing,
      '& .MuiChip-label': {
        fontFamily: calendarSnapshotChipTypography.fontFamily,
        fontSize: calendarSnapshotChipTypography.fontSize,
        fontWeight: calendarSnapshotChipTypography.fontWeight,
        lineHeight: calendarSnapshotChipTypography.lineHeight,
        letterSpacing: calendarSnapshotChipTypography.letterSpacing,
        color: 'inherit',
        px: '8px',
      },
      '& .MuiChip-icon': {
        color: 'inherit',
        ml: 0.5,
        mr: 0.5,
        fontSize: '14px',
      },
      '&:hover': {
        backgroundColor: isSelected ? toneStyle.selectedBg : '#FFFFFF',
      },
    };

    if (!chipSx) {
      return baseSx;
    }

    return Array.isArray(chipSx)
      ? chipSx.reduce<SystemStyleObject<Theme>>((acc, current) => ({ ...acc, ...current }), baseSx)
      : { ...baseSx, ...chipSx };
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1, flexWrap: 'wrap' }}>
      {mediaToneOptions.map((toneObj, index) => {
        const isSelected = localSelectedTones.includes(toneObj.name);
        return (
          <Chip
            sx={getChipSx(toneObj.name, isSelected)}
            color={isSelected ? ToneColorMap[toneObj.name] : 'default'}
            variant="outlined"
            key={`${toneObj.name}-${index}`}
            label={toneObj.name}
            onClick={() => handleChipClick(toneObj.name)}
            icon={getChipIcon(toneObj.name)}
            data-cy={`tone-${toneObj._id}`}
          />
        );
      })}
    </Box>
  );
}
